import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { MODALITIES, relativeMinutes, type Modality } from "@/lib/modality";
import { BottomNav } from "@/components/BottomNav";

export const Route = createFileRoute("/profile_/rides")({
  component: MyRidesPage,
});

interface Row {
  id: string;
  modality: Modality;
  start_time: string;
  estimated_km: number | null;
  start_address: string | null;
}

function MyRidesPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [rides, setRides] = useState<Row[]>([]);

  useEffect(() => { if (!loading && !user) navigate({ to: "/auth" }); }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from("rides")
        .select("id, modality, start_time, estimated_km, start_address")
        .eq("creator_id", user.id)
        .order("start_time", { ascending: false });
      setRides((data as Row[]) || []);
    })();
  }, [user]);

  return (
    <div className="h-[100dvh] overflow-y-auto bg-background fade-in" style={{ paddingBottom: 80 }}>
      <header className="flex items-center gap-3 px-5 py-4" style={{ paddingTop: 'max(env(safe-area-inset-top), 16px)' }}>
        <button onClick={() => navigate({ to: "/profile" })} className="rounded-full bg-surface p-2" aria-label="Volver">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-display text-2xl">Mis salidas</h1>
      </header>

      <div className="px-5">
        {rides.length === 0 ? (
          <p className="mt-8 text-center text-sm text-white/60">Aún no has creado ninguna salida</p>
        ) : (
          <div className="space-y-2">
            {rides.map((r) => {
              const m = MODALITIES.find((mm) => mm.value === r.modality);
              const mColor = r.modality === "road" ? "#F5C518" : r.modality === "gravel" ? "#e07b3a" : "#4caf7d";
              return (
                <Link
                  key={r.id} to="/ride/$rideId" params={{ rideId: r.id }}
                  className="flex items-center justify-between rounded-xl bg-[#1a1a1a] border border-[#2a2a2a] px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                        style={{ background: `color-mix(in oklab, ${mColor} 22%, transparent)`, color: mColor }}
                      >{m?.label}</span>
                      <span className="truncate text-xs text-white/70">{r.start_address?.split(",")[0] ?? "—"}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{relativeMinutes(r.start_time)}</div>
                  </div>
                  <div className="text-sm font-medium">{r.estimated_km ? `${r.estimated_km} km` : "—"}</div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
